import React from 'react';
import PropTypes from 'prop-types';
import { usePageViewTracking } from 'components/core/library/tracking-utils/tracking/withPageViewTracking';

import { LandingHero } from 'components/landing/LandingHero/LandingHero';
import { Page } from 'components/core/Page';
import { H1, H2, H3 } from 'components/core/Headline';
import { Paragraph } from 'components/core/Paragraph';
import { Button } from 'components/core/Button';
import { Link } from 'components/core/Link';
import { Card } from 'components/core/Card';
import { Image } from 'components/core/Image';
import { IconText } from 'components/core/IconText/IconText';
import { LandingSection } from 'components/landing/LandingSection';
import { useSelector } from 'react-redux';

import alertIconImg from 'assets/images/icons/clock.svg';
import StaticCardData from './StaticCardData';
import { Helmet } from "react-helmet-async";
import './MovingSuppliesPage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';


export function MovingSuppliesPage({ cardData = StaticCardData, ...props }) {
	const baseUrl = useSelector((state) => state.app.baseUrl);

	usePageViewTracking(PAGE_TYPES.PSP, { ...props, baseUrl });
	return (
		<Page
			className="moving-supplies-page psp-landing-page"
			pageType={PAGE_TYPES.PSP}
		>
			<Helmet>
				<link
					rel="canonical"
					href={`${baseUrl}/moving-supplies.html`}
				/>
			</Helmet>
			<LandingHero
				dark
				withSearchBox={false}
				headline="Moving Supplies"
				subheading="SpareFoot and uBoxes have teamed up to bring you everything you need for your next move.
				Get durable moving and packing supplies at a low price, and make this move the easiest one yet."
			>
				<Button
					href="https://shareasale.com/r.cfm?b=883129&u=3346460&m=66601&urllink=www%2Eboxengine%2Ecom%2Fmoving%2Dkits&afftrack="
					target="_blank"
					rel="noopener noreferrer"
				>
					Learn more
				</Button>
			</LandingHero>

			<div className="section-content moving-kits">
				<H1 className="hero-header">Moving kits</H1>
				<Paragraph>
					Get a Smart Move Kit expertly stocked with everything you
					need to move.
				</Paragraph>
				<Button
					href="https://shareasale.com/r.cfm?b=883129&u=3346460&m=66601&urllink=www%2Eboxengine%2Ecom%2Fmoving%2Dkits&afftrack="
					target="_blank"
					rel="noopener noreferrer"
				>
					Learn more
				</Button>
			</div>

			<div className="section-content moving-supplies-content">
				<H1 className="hero-header">Featured products</H1>
				<IconText
					type="urgent"
					src={alertIconImg}
				>
					Delivery for all supplies is 2-3 days from purchase date.
				</IconText>

				<div className="card-group">
					{cardData.map((card) => (
						<Card key={card.title}>
							<div className="top">
								<Image
									flexImage
									src={card.image}
									alt={card.title}
								/>
								<div className="info">
									<H2
										size="small"
										strong
									>
										{card.title}
									</H2>
									<Paragraph
										light
										className="desc"
										size="tiny"
									>
										{card.text}
									</Paragraph>
								</div>
							</div>
							<div className="bottom">
								<Button
									fullWidth
									href={card.link}
									target="_blank"
									rel="noopener noreferrer"
								>
									Shop now
								</Button>
							</div>
						</Card>
					))}
				</div>
			</div>

			<div className="section-content static-content">
				<LandingSection>
					<H3>Get the most out of your moving boxes</H3>
					<Paragraph>
						When it comes to moving, not all moving boxes are
						created equal. SpareFoot brings you the most durable and
						high-quality moving boxes to ensure your stuff is safe,
						protected and easy to move from one place to another.
					</Paragraph>
					<Paragraph>
						Find the best moving box for you, whether you’re looking
						for standard moving boxes to specialty boxes for
						clothing, flat-screen TVs, kitchen gadgets, glassware,
						artwork or mirrors.
					</Paragraph>
					<Link
						href="https://shareasale.com/r.cfm?b=883129&u=3346460&m=66601&urllink=www%2Eboxengine%2Ecom%2Fmoving%2Dboxes%2Fmedium%2Dboxes&afftrack="
						target="_blank"
						rel="noopener noreferrer"
					>
						Shop moving boxes
					</Link>
				</LandingSection>
				<LandingSection>
					<H3>SpareFoot Moving and Packing Supplies</H3>
					<Paragraph>
						Moving can be stressful, but it can also be a breeze
						when you’ve got the right tools. SpareFoot brings you
						the best moving and packing supplies at the most
						affordable price. Get moving labels, mattress bags,
						bubble wrap, moving blankets, moving pads and more to
						protect your stuff whether you’re moving, storing or
						organizing.
					</Paragraph>
					<Link
						href="https://shareasale.com/r.cfm?b=883129&u=3346460&m=66601&urllink=www%2Eboxengine%2Ecom%2Fmoving%2Dsupplies%2Fother&afftrack="
						target="_blank"
						rel="noopener noreferrer"
					>
						Shop moving and packing supplies kits
					</Link>
				</LandingSection>
			</div>
		</Page>
	);
}

MovingSuppliesPage.propTypes = {
	cardData: PropTypes.arrayOf(PropTypes.object),
	baseUrl:PropTypes.string
};

export default MovingSuppliesPage;
